const groupPrefix = createApiRoute(ApiModule.CLIENT, ApiDataModel.CLIENTS)
const groupPrefixEmployee = createApiRoute(ApiModule.AUTH, ApiAuthorization.EMPLOYEE, ApiDataModel.CLIENTS)
const groupPrefixClient = createApiRoute(ApiModule.CLIENT, ApiAuthorization.EMPLOYEE)
const groupPrefixClients = createApiRoute(ApiModule.CLIENT, ApiAuthorization.EMPLOYEE, ApiDataModel.CLIENTS)

const CLIENT_HISTORY_FILTERS_QUERY_KEY = 'client-history-filters-query' as const
export const clientHistoryFiltersQueryKey = { ...useQueryKeyFactory(CLIENT_HISTORY_FILTERS_QUERY_KEY) }

export const apiClient = {
	createClient: async (obj: Partial<IClientModel>) => {
		const { data } = await apiCommon<IClient>(`${groupPrefix}`, {
			method: 'POST',
			body: clientToJSON(obj),
		})
		return clientFromJSON(data)
	},

	updateClient: async (clientUUID: string, obj: Partial<IClientModel>) => {
		const { data } = await apiCommon<IClient>(`${groupPrefix}/${clientUUID}`, {
			method: 'PATCH',
			body: clientToJSON(obj),
		})
		return clientFromJSON(data)
	},
	updateClientMarketingConsents: async (
		clientId: number,
		payload: Partial<Record<TClientMarketingConsentField, false>>,
	): Promise<void> => {
		await apiCommon(`${groupPrefixClients}/${clientId}/marketing-consents`, {
			method: 'PATCH',
			body: payload,
		})
	},
	fetchClients: async ({
		activeFilter,
		cityFilter,
		search,
		page = 1,
		perPage = 25,
		id,
		uuid,
		filters,
	}: {
		activeFilter?: string[]
		cityFilter?: string[]
		search?: string
		page?: number
		perPage?: number
		id?: string[]
		uuid?: string[]
		filters?: IApiFilters
	} = {}) => {
		const { data, meta } = await apiCommon<IClient[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery({ page, perPage }, { active: activeFilter, city: cityFilter, search, id, uuid, ...filters }),
		})
		return { data: data.map(d => clientFromJSON(d)), meta }
	},
	fetchClientsInfluencers: async (): Promise<IClientModel[]> => {
		const { data } = await apiCommon<IClient[]>(`${groupPrefix}/influencers`, { method: 'GET' })

		return data.map(clientFromJSON)
	},
	fetchClientDetails: async (clientUUID: string) => {
		const { data } = await apiCommon<IClient>(`${groupPrefix}/${clientUUID}`, { method: 'GET' })
		return clientFromJSON(data)
	},
	fetchClientsCities: async () => {
		const { data } = await apiCommon<string[]>(`${groupPrefix}/cities`, { method: 'GET' })
		return data
	},
	deleteClient: async (clientUUID: string) =>
		await apiCommon<IEmployee>(`${groupPrefixEmployee}/${clientUUID}`, { method: 'DELETE' }),
	resendClientEmailVerification: async (clientUUID: string) =>
		await apiCommon<IEmployee>(`auth/api/employee/clients/resend-email-verification/${clientUUID}`, {
			method: 'POST',
		}),
	recommendationsFromFriends: async (clientId: number): Promise<IReferralStatsModel> => {
		const { data } = await apiCommon<IReferralStats>(`${groupPrefixClients}/${clientId}/referral-stats`, {
			method: 'GET',
		})
		return referrakStatsToJSON(data)
	},
	fetchProgram800Status: async (clientId: number): Promise<IClientProgram800StatusModel> => {
		const response = (await apiCommon<IClientProgram800Enrollment | { message: string }>(
			`${groupPrefixClients}/${clientId}/program-800`,
			{ method: 'GET' },
		)) as IClientProgram800ApiResponse
		return clientProgram800StatusFromJSON(response)
	},
	fetchClientHistory: async (
		clientUUID: string,
		search: IApiSearch,
		{ page, perPage = 25 }: IApiPagination,
		filters?: IApiFilters,
	): Promise<IApiResponseModel<IClientBusinessEventModel[]>> => {
		const searchFilter = createSearchQuery(search.query, search.type)

		const { data, meta } = await apiCommon<IClientBusinessEvent[]>(
			`${groupPrefixClient}/client-activity-logs/${clientUUID}`,
			{
				method: 'GET',
				...createQuery({ page, perPage }, { ...searchFilter, ...filters }, undefined),
			},
		)

		return { data: data.map(ClientBusinessEvent), meta }
	},
	fetchClientHistoryFilters: async (): Promise<IClientBusinessEventFilters> => {
		const fetchFn = async (): Promise<IClientBusinessEventFilters> =>
			await apiCommonDirect<IClientBusinessEventFilters>(`${groupPrefixClient}/client-activity-logs/filters`, {
				method: 'GET',
			})

		const config = () => ({
			queryKey: clientHistoryFiltersQueryKey.list(),
			queryFn: async () => await fetchFn(),
		})

		return await useQueryClientService().fetchQuery<IClientBusinessEventFilters>(config)
	},
}
